import { useEffect, useState, useContext, useRef } from "react";
import { localStorage, ViewerContext } from "dash-ui";
import { AccountStatusesEnum } from "dash-common";
import {
  useGetCurrentAccountTrialProductSubscriptionsQuery,
  useGetCurrentAccountProductSubscriptionQuery,
  useGetCurrentBrokerLazyQuery,
  useObserveCurrentAccountChangesSubscription,
  useObserveCurrentAccountProductSubscriptionSubscription,
  useObserveSubscriptionProductsChangesSubscription,
  useGetAccountLazyQuery,
} from "gql/generated.graphql";
import { useLogout } from "common/hooks/useLogout/useLogout";
import * as Sentry from "@sentry/react";
import { resetIdentity } from "shared/analytics";

const useInitViewer = () => {
  const [isReady, setIsReady] = useState(false);
  const {
    account: contextAccount,
    tokens,
    setAccount,
    setBroker,
    setProfile,
    setTokens,
    setSubscription,
    setUserHasTrialed,
  } = useContext(ViewerContext);
  const { refreshToken } = tokens;
  const [hasInitialLoadStarted, setHasInitialLoadStarted] = useState(false);

  const [getBroker, { loading: isViewerLoading, data: accountData, error }] =
    useGetCurrentBrokerLazyQuery({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      // Don't let Apollo's error handler trigger logout for this query
      errorPolicy: "all",
    });
  const broker = accountData?.getCurrentBroker;
  const { account, profile, subscriptionType } = broker || {};
  const [logout] = useLogout();

  const [getSSO, { data: ssoAccountData, loading: isSSOLoading }] =
    useGetAccountLazyQuery({
      errorPolicy: "all",
    });

  const ssoAccount = ssoAccountData?.getAccount;

  const {
    data: subscriptionData,
    loading: isViewerSubscriptionLoading,
    refetch: subscriptionRefetch,
  } = useGetCurrentAccountProductSubscriptionQuery({
    skip: !refreshToken,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const subscription = subscriptionData?.getCurrentAccountProductSubscription;

  const {
    data: trialSubscriptionsData,
    loading: isViewerTrialSubscriptionsLoading,
    refetch: trialSubscriptionsRefetch,
  } = useGetCurrentAccountTrialProductSubscriptionsQuery({
    skip: !refreshToken,
    fetchPolicy: "network-only",
  });
  const trialSubscriptions =
    trialSubscriptionsData?.getCurrentAccountTrialProductSubscriptions
      ?.collection || [];
  // FIXME: this doesn't support trial by product. change in the future.
  const userHasTrialed = !!trialSubscriptions.length;

  // Get active trial subscription if it exists
  const activeTrialSubscription = trialSubscriptions.find(
    (trial) => trial.status === "TRIALING"
  );

  // Detect if the base subscription is a fully canceled trial (status: CANCELED with trialEndAt).
  // This is different from SCHEDULED_CANCELLATION - it means the trial period ended or was immediately canceled.
  // We use this to avoid showing canceled trials as the current plan in ViewerContext.
  const isCanceledTrial =
    subscription?.trialEndAt && subscription.status === "CANCELED";

  useObserveSubscriptionProductsChangesSubscription({
    onData() {
      subscriptionRefetch();
      trialSubscriptionsRefetch();
    },
  });

  useObserveCurrentAccountProductSubscriptionSubscription({
    onData({ data }) {
      if (setSubscription) {
        setSubscription(
          data.data?.observeCurrentAccountProductSubscription.document
        );
      }
    },
  });

  useObserveCurrentAccountChangesSubscription({
    onData: ({ data }) => {
      const account = data?.data?.observeCurrentAccountChanges?.account;
      if (account) {
        if (account.status !== AccountStatusesEnum.ACTIVE) {
          logout();
          return;
        }
        if (setAccount) {
          setAccount(account);
        }
      }
    },
  });

  // When a trial is canceled, refetch to get the underlying base subscription.
  // Example: User cancels Exchange trial → refetch reveals the PRO subscription underneath.
  // This ensures ViewerContext switches from showing the canceled trial to the active base plan.
  // Use a ref to prevent redundant refetches if the backend keeps returning the same canceled trial.
  const hasRefetchedCanceledTrial = useRef(false);

  useEffect(() => {
    if (isCanceledTrial && !hasRefetchedCanceledTrial.current) {
      hasRefetchedCanceledTrial.current = true;
      subscriptionRefetch();
    } else if (!isCanceledTrial && hasRefetchedCanceledTrial.current) {
      // Reset flag when trial is no longer canceled (e.g., user gets a new trial)
      hasRefetchedCanceledTrial.current = false;
    }
  }, [isCanceledTrial, subscriptionRefetch]);

  // Refetch subscription when user returns to the tab (e.g., from Stripe portal)
  // This ensures ViewerContext has fresh data after external subscription updates
  // Uses a 5-minute cooldown to prevent excessive refetching on rapid tab switches
  const lastRefetchTime = useRef<number>(0);
  const REFETCH_COOLDOWN = 5 * 60 * 1000; // 5 minutes in milliseconds

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        if (now - lastRefetchTime.current > REFETCH_COOLDOWN) {
          subscriptionRefetch();
          trialSubscriptionsRefetch();
          lastRefetchTime.current = now;
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [subscriptionRefetch, trialSubscriptionsRefetch]);

  // Execute queries when we have valid tokens
  useEffect(() => {
    const currentLocalRefreshToken = localStorage.getRefreshToken();

    // Execute if we have a refresh token (with or without access token)
    if (refreshToken && currentLocalRefreshToken) {
      // Always fetch account when we have a refresh token
      // The token refresh link will handle expired access tokens
      setHasInitialLoadStarted(true);
      getBroker();
      getSSO();
    } else if (!refreshToken && !currentLocalRefreshToken) {
      // No tokens at all - user is not logged in
      setHasInitialLoadStarted(true);
      setIsReady(true);
    }
  }, [refreshToken, getBroker, getSSO]);

  // FIXME: re-check next line
  useEffect(() => {
    if (
      hasInitialLoadStarted &&
      !isViewerLoading &&
      !isViewerSubscriptionLoading &&
      !isViewerTrialSubscriptionsLoading &&
      !isSSOLoading
    ) {
      if (account && setAccount) {
        setAccount(account);
      }
      if (ssoAccount && setAccount) {
        setAccount(ssoAccount);
      }
      if (profile && setProfile) {
        setProfile(profile);
      }
      if (broker && setBroker) {
        setBroker(broker);
        Sentry.setUser({
          id: broker._id,
          email: account?.email,
          subscriptionType,
        });
      }
      // Determine which subscription to show as "current" in ViewerContext.
      // Priority: 1) Active trial (TRIALING status), 2) Base subscription (if not a canceled trial), 3) null
      // This prevents canceled trials from appearing as the current plan, ensuring the base subscription shows instead.
      const currentSubscription =
        activeTrialSubscription || (!isCanceledTrial ? subscription : null);
      if (currentSubscription && setSubscription) {
        setSubscription(currentSubscription);
      }
      if (userHasTrialed && setUserHasTrialed) {
        setUserHasTrialed(userHasTrialed);
      }
      if (error) {
        // Only logout if we have an auth error and a refresh token
        // This prevents logout during flows where only temp tokens exist
        const hasRefreshToken = localStorage.getRefreshToken();
        if (error.message === "Unauthorized" && hasRefreshToken) {
          logout();
          return;
        }
        console.error("---useInitUser -->", error);
      }
      // Set ready after all data is loaded and context is updated
      setIsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInitialLoadStarted,
    isViewerLoading,
    isViewerSubscriptionLoading,
    isViewerTrialSubscriptionsLoading,
    setAccount,
    account,
    ssoAccount,
    broker,
    setBroker,
    profile,
    setProfile,
    subscription,
    activeTrialSubscription,
    setSubscription,
    userHasTrialed,
    setUserHasTrialed,
    error,
    isSSOLoading,
    refreshToken,
  ]);

  useEffect(() => {
    const onLocalStorageChanged = () => {
      const newAccessToken = localStorage.getAccessToken() || "";
      const newRefreshToken = localStorage.getRefreshToken() || "";
      const newRefreshTokenExpiry = localStorage.getRefreshTokenExpiry() || "";

      // Update tokens in context
      if (setTokens) {
        setTokens({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
          refreshTokenExpiry: newRefreshTokenExpiry,
        });
      }

      // Clear account if tokens are removed (logout)
      if (!newAccessToken && !newRefreshToken && contextAccount) {
        // Only logout if we still have an account (prevents infinite loop)
        if (setAccount) {
          setAccount(null);
        }
        if (setBroker) {
          setBroker(null);
        }
        if (setProfile) {
          setProfile(null);
        }
        if (setSubscription) {
          setSubscription(null);
        }
        resetIdentity();
      }
    };

    window.addEventListener("storage", onLocalStorageChanged);

    // Also check immediately on mount in case tokens were cleared before this component mounted
    onLocalStorageChanged();

    return () => {
      window.removeEventListener("storage", onLocalStorageChanged);
    };
  }, [
    setTokens,
    setAccount,
    setBroker,
    setProfile,
    setSubscription,
    contextAccount,
  ]);
  return [isReady];
};
export default useInitViewer;
